import React from 'react';
import { Box, Flex, SimpleGrid, Switch, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Bank, Scales, TrendDown, TrendUp } from 'phosphor-react';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import { useFinanceiroFluxo, useFinanceiroSaude } from '../../../../hooks/useFinanceiro';
import { AnimatedBar } from '../components/AnimatedBar';
import { isContaInterna, tipoLabel } from '../components/contaTipo';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const fmtMes = (mes: string) => {
  const [y, m] = (mes || '').split('-');
  return y && m ? `${m}/${y.slice(2)}` : mes;
};

const compact = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1).replace('.', ',')}M`;
  if (abs >= 1e3) return `${Math.round(v / 1e3)}k`;
  return String(Math.round(v));
};

const FAIXA = {
  bom: { cor: '#16A34A', label: 'Saudável' },
  atencao: { cor: '#D97706', label: 'Atenção' },
  critico: { cor: '#DC2626', label: 'Crítico' },
} as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FluxoTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      bg="white"
      border="1px solid rgba(15,23,42,0.1)"
      borderRadius="md"
      boxShadow="lg"
      p={2.5}
      fontSize="xs"
    >
      <Text fontWeight={700} color="#0F172A" mb={1}>
        {label}
      </Text>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {payload.map((p: any) => (
        <Flex key={p.dataKey} align="center" gap={2}>
          <Box w="8px" h="8px" borderRadius="sm" bg={p.color} />
          <Text color="#64748B">{p.name}:</Text>
          <Text color="#0F172A" fontWeight={600}>
            {brl(p.value)}
          </Text>
        </Flex>
      ))}
    </Box>
  );
};

const ScoreRing = ({ score, cor }: { score: number; cor: string }) => {
  const r = 36;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, score)) / 100);
  return (
    <Box position="relative" w="84px" h="84px" flexShrink={0}>
      <svg width="84" height="84" viewBox="0 0 84 84">
        <circle cx="42" cy="42" r={r} fill="none" stroke="rgba(15,23,42,0.08)" strokeWidth="7" />
        <g transform="rotate(-90 42 42)">
          <motion.circle
            cx="42"
            cy="42"
            r={r}
            fill="none"
            stroke={cor}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={c}
            initial={{ strokeDashoffset: c }}
            animate={{ strokeDashoffset: off }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          />
        </g>
      </svg>
      <Flex position="absolute" inset={0} align="center" justify="center">
        <Text fontSize="24px" fontWeight={800} color="#0F172A">
          {score}
        </Text>
      </Flex>
    </Box>
  );
};

export const FinanceiroFluxoPage = () => {
  const f = useFinanceiroFilters({ defaultPeriod: 'year' });
  const { data, isError } = useFinanceiroFluxo({ base: 'competencia', ...f.apiParams });
  const { data: saude } = useFinanceiroSaude({ base: 'competencia', ...f.apiParams });

  const faixa = FAIXA[(saude?.faixa as keyof typeof FAIXA) ?? 'atencao'] ?? FAIXA.atencao;
  const previsao = saude?.previsao ?? [];

  const [hideInternal, setHideInternal] = React.useState(true);

  const meses = data?.meses ?? [];
  const totalEntradas = meses.reduce((s, m) => s + m.entradas, 0);
  const totalSaidas = meses.reduce((s, m) => s + m.saidas, 0);
  const resultado = totalEntradas - totalSaidas;

  // Contas internas (Outros/Aplicação) são movimentos não-operacionais. Quando
  // ocultadas, saem da lista E do saldo que alimenta o KPI e o fôlego de caixa.
  const todasContas = data?.saldo_contas ?? [];
  const contasInternas = todasContas.filter(c => isContaInterna(c.tipo));
  const contas = hideInternal ? todasContas.filter(c => !isContaInterna(c.tipo)) : todasContas;
  const saldoTotal = contas.reduce((s, c) => s + c.saldo, 0);
  const maxConta = Math.max(1, ...contas.map(c => Math.abs(c.saldo)));

  // Fôlego de caixa: saldo atual ÷ ritmo médio de saídas mensais.
  const mesesComSaida = meses.filter(m => m.saidas > 0).length || meses.length;
  const avgSaidas = mesesComSaida > 0 ? totalSaidas / mesesComSaida : 0;
  const runway = avgSaidas > 0 ? saldoTotal / avgSaidas : 0;

  const chartMeses = meses.map(m => ({
    mes: fmtMes(m.mes),
    entradas: m.entradas,
    saidas: m.saidas,
    resultado: m.resultado,
  }));
  const chartPrev = previsao.map(p => ({
    mes: fmtMes(p.mes),
    acumulado: p.acumulado,
    entradas: p.entradas_previstas,
    saidas: p.saidas_previstas,
  }));

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Caixa & Previsão"
          titulo="Fluxo de caixa"
          subtitulo="Realizado por mês, saldo das contas, saúde e projeção do caixa"
          icon={<Bank size={24} weight="fill" />}
          cor="#0EA5E9"
          info={
            <Text>
              Realizado por mês (regime de caixa, por data de pagamento) + <b>score de saúde</b>{' '}
              (margem, inadimplência, cobertura) e <b>projeção</b> de caixa pelos vencimentos
              futuros.
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar o fluxo de caixa.</Text>
          </GlassCard>
        )}

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          <KpiCard
            label="Saldo em contas"
            valor={saldoTotal}
            color="#1B88F5"
            icon={<Bank size={20} weight="duotone" />}
            index={0}
            info={
              <InfoPopover title="Saldo em contas">
                Soma do saldo atual das contas financeiras ativas (ca_contas_financeiras). É um
                retrato de <b>hoje</b>, não depende do período. Por padrão <b>exclui</b> contas
                internas (Outros/Aplicação/Cartão) — alterne em &quot;Saldo por conta&quot;.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Entradas (período)"
            valor={totalEntradas}
            color="#16A34A"
            icon={<TrendUp size={20} weight="duotone" />}
            index={1}
            info={
              <InfoPopover title="Entradas">
                Total <b>recebido</b> (RECEITA paga) nos meses do período selecionado.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Saídas (período)"
            valor={totalSaidas}
            color="#DC2626"
            icon={<TrendDown size={20} weight="duotone" />}
            index={2}
            info={
              <InfoPopover title="Saídas">
                Total <b>pago</b> (DESPESA paga) nos meses do período selecionado.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Resultado (período)"
            valor={resultado}
            color={resultado >= 0 ? '#16A34A' : '#DC2626'}
            icon={<Scales size={20} weight="duotone" />}
            index={3}
            info={
              <InfoPopover title="Resultado de caixa">
                Entradas − saídas do período. Positivo = o caixa cresceu no recorte; negativo =
                consumiu saldo.
              </InfoPopover>
            }
          />
        </SimpleGrid>

        {/* Gráfico — fluxo mensal realizado */}
        <GlassCard intensity="strong" p={{ base: 3, md: 6 }} mb={5}>
          <Flex align="center" gap={1} mb={4} wrap="wrap">
            <Text fontWeight={700} color="#0F172A">
              Fluxo mensal —{' '}
              <Text as="span" color="#16A34A">
                entradas
              </Text>{' '}
              ×{' '}
              <Text as="span" color="#DC2626">
                saídas
              </Text>{' '}
              ·{' '}
              <Text as="span" color="#1B88F5">
                resultado
              </Text>
            </Text>
          </Flex>
          {chartMeses.length === 0 ? (
            <Text fontSize="sm" color="#94A3B8" py={8} textAlign="center">
              Sem movimentação no período.
            </Text>
          ) : (
            <Box h="300px">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartMeses} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(15,23,42,0.06)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="mes"
                    tick={{ fontSize: 11, fill: '#94A3B8' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={compact}
                    tick={{ fontSize: 11, fill: '#94A3B8' }}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                  />
                  <Tooltip content={<FluxoTooltip />} cursor={{ fill: 'rgba(15,23,42,0.03)' }} />
                  <Bar
                    dataKey="entradas"
                    name="Entradas"
                    fill="#16A34A"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={26}
                  />
                  <Bar
                    dataKey="saidas"
                    name="Saídas"
                    fill="#DC2626"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={26}
                  />
                  <Line
                    type="monotone"
                    dataKey="resultado"
                    name="Resultado"
                    stroke="#1B88F5"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: '#1B88F5' }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          )}
        </GlassCard>

        {/* Saúde + Projeção */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
          <GlassCard intensity="strong" p={6}>
            <Flex align="center" gap={1} mb={4}>
              <Text fontWeight={700} color="#0F172A">
                Saúde financeira
              </Text>
              <InfoPopover title="Score de saúde">
                Média de 3 indicadores (0–100): <b>margem</b>, <b>inadimplência</b> e{' '}
                <b>cobertura de caixa</b> (a receber ÷ a pagar). O saldo das contas não entra no
                score — é um retrato de hoje, não do período.
              </InfoPopover>
            </Flex>
            <Flex align="center" gap={5} mb={4}>
              <ScoreRing score={saude?.score ?? 0} cor={faixa.cor} />
              <Box>
                <Text fontSize="lg" fontWeight={700} color={faixa.cor}>
                  {faixa.label}
                </Text>
                <Text fontSize="xs" color="#94A3B8">
                  score de 0 a 100
                </Text>
              </Box>
            </Flex>
            <Flex direction="column" gap={3}>
              {(saude?.metricas ?? []).map(m => {
                let corBarra = '#DC2626';
                if (m.score >= 70) corBarra = '#16A34A';
                else if (m.score >= 40) corBarra = '#D97706';
                return (
                  <Box key={m.chave}>
                    <Flex justify="space-between" fontSize="xs" mb={1}>
                      <Text color="#0F172A" fontWeight={600}>
                        {m.label}
                      </Text>
                      <Text color="#94A3B8">{m.detalhe}</Text>
                    </Flex>
                    <AnimatedBar pct={Math.max(2, m.score)} cor={corBarra} h="6px" />
                  </Box>
                );
              })}
            </Flex>
          </GlassCard>

          <GlassCard intensity="medium" p={6}>
            <Flex align="center" gap={1} mb={1}>
              <Text fontWeight={700} color="#0F172A">
                Projeção de saldo
              </Text>
              <InfoPopover title="Projeção de caixa">
                Saldo <b>acumulado projetado</b> = títulos em aberto (<i>nao_pago</i>) com
                vencimento futuro, mês a mês. Cruza a linha do zero quando o caixa fica negativo.
              </InfoPopover>
            </Flex>
            <Flex align="baseline" gap={2} mb={3}>
              <Text fontSize="11px" color="#94A3B8">
                Fôlego de caixa:
              </Text>
              <Text fontSize="sm" fontWeight={700} color={runway >= 3 ? '#16A34A' : '#D97706'}>
                {runway > 0 ? `${runway.toFixed(1).replace('.', ',')} meses` : '—'}
              </Text>
              <Text fontSize="11px" color="#94A3B8">
                ao ritmo atual de saídas
              </Text>
            </Flex>
            {chartPrev.length === 0 ? (
              <Text fontSize="sm" color="#94A3B8" py={4}>
                Sem títulos futuros em aberto.
              </Text>
            ) : (
              <Box h="200px">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartPrev} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gAcum" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(15,23,42,0.06)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 11, fill: '#94A3B8' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={compact}
                      tick={{ fontSize: 11, fill: '#94A3B8' }}
                      axisLine={false}
                      tickLine={false}
                      width={48}
                    />
                    <Tooltip content={<FluxoTooltip />} />
                    <ReferenceLine y={0} stroke="#DC2626" strokeDasharray="4 4" />
                    <Area
                      type="monotone"
                      dataKey="acumulado"
                      name="Saldo projetado"
                      stroke="#0EA5E9"
                      strokeWidth={2}
                      fill="url(#gAcum)"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            )}
          </GlassCard>
        </SimpleGrid>

        {/* Saldo por conta */}
        <GlassCard intensity="medium" p={6}>
          <Flex align="center" justify="space-between" mb={4} wrap="wrap" gap={3}>
            <Flex align="center" gap={3} wrap="wrap">
              <Text fontWeight={700} color="#0F172A">
                Saldo por conta
              </Text>
              {contasInternas.length > 0 && (
                <Flex align="center" gap={2}>
                  <Switch
                    size="sm"
                    colorScheme="blue"
                    isChecked={hideInternal}
                    onChange={e => setHideInternal(e.target.checked)}
                  />
                  <Text fontSize="11px" color="#94A3B8">
                    Ocultar internas ({contasInternas.length})
                  </Text>
                  <InfoPopover title="Contas internas">
                    Contas do tipo <b>Outros</b>, <b>Aplicação</b> e <b>Cartão de crédito</b> são
                    movimentos internos, não caixa operacional. Ocultá-las as remove desta lista e
                    as exclui do <b>Saldo em contas</b> e do <b>fôlego de caixa</b>.
                  </InfoPopover>
                </Flex>
              )}
            </Flex>
            <Text fontSize="sm" color="#64748B">
              Total: <b>{brl(saldoTotal)}</b>
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={3}>
            {contas.map(c => (
              <Box key={`conta-${c.nome}`}>
                <Flex justify="space-between" align="center" gap={2} mb={1}>
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight={600} color="#0F172A" noOfLines={1}>
                      {c.nome || '—'}
                    </Text>
                    {c.tipo && (
                      <Text fontSize="11px" color="#94A3B8">
                        {tipoLabel(c.tipo)}
                      </Text>
                    )}
                  </Box>
                  <Text
                    fontSize="sm"
                    fontWeight={700}
                    color={c.saldo >= 0 ? '#0F172A' : '#DC2626'}
                    whiteSpace="nowrap"
                  >
                    {brl(c.saldo)}
                  </Text>
                </Flex>
                <AnimatedBar
                  pct={Math.max(2, (Math.abs(c.saldo) / maxConta) * 100)}
                  cor={c.saldo >= 0 ? '#0EA5E9' : '#DC2626'}
                  h="5px"
                />
              </Box>
            ))}
            {contas.length === 0 && (
              <Text fontSize="sm" color="#94A3B8">
                {hideInternal && contasInternas.length > 0
                  ? 'Apenas contas internas (Outros/Aplicação/Cartão) — desligue o filtro para exibir.'
                  : 'Sem contas financeiras.'}
              </Text>
            )}
          </SimpleGrid>
        </GlassCard>
      </Box>
    </GlassPageBg>
  );
};
